import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, Send, Copy, Download, RefreshCw, Image as ImageIcon,
  MessageSquare, Hash, Upload, X, Film, Video
} from "lucide-react";

const TONES = ["Impactante", "Urgente", "Confiável", "Descontraído", "Institucional", "Promocional"];
const AUDIENCES = [
  "Proprietários de carros", "Motociclistas", "Frotistas / empresas",
  "Caminhoneiros", "Todos os motoristas",
];
const IMAGE_ASPECTS = [
  { v: "1:1", label: "Feed 1:1" },
  { v: "9:16", label: "Story 9:16" },
  { v: "16:9", label: "Web 16:9" },
];
const VIDEO_SIZES = [
  { v: "1024x1792", label: "Vertical 9:16" },
  { v: "1024x1024", label: "Quadrado 1:1" },
  { v: "1792x1024", label: "Horizontal 16:9" },
  { v: "1280x720", label: "HD 16:9" },
];
const VIDEO_DURATIONS = [4, 8, 12];
const VIDEO_MODELS = [
  { v: "sora-2", label: "Sora 2 · Rápido" },
  { v: "sora-2-pro", label: "Sora 2 Pro · Máxima qualidade" },
];

const PLACEHOLDER = "Ex: Plano de rastreamento veicular garantido a R$100/mês. Cobertura de até 10 mil reais da tabela FIPE. Guincho, chaveiro, pane seca inclusos. Foco em motos.";

const POLL_INTERVAL = 3000;

function Stat({ label, value, testid }) {
  return (
    <div className="avsl-card p-5" data-testid={testid}>
      <div className="overline">{label}</div>
      <div className="font-mono-tech text-3xl mt-2">{value}</div>
    </div>
  );
}

async function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function Generator() {
  const [mode, setMode] = useState("image"); // image | video
  const [prompt, setPrompt] = useState("");
  const [tone, setTone] = useState("Impactante");
  const [audience, setAudience] = useState("Proprietários de carros");
  const [cta, setCta] = useState("Contrate agora");
  // image
  const [aspect, setAspect] = useState("1:1");
  const [refImage, setRefImage] = useState(null); // data URL
  // video
  const [videoModel, setVideoModel] = useState("sora-2");
  const [videoDuration, setVideoDuration] = useState(4);
  const [videoSize, setVideoSize] = useState("1024x1792");

  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [ad, setAd] = useState(null);
  const [stats, setStats] = useState({ total_ads: 0, today: 0, videos: 0 });
  const pollRef = useRef(null);
  const fileRef = useRef(null);

  const loadStats = async () => {
    try {
      const { data } = await api.get("/stats");
      setStats(data);
    } catch {}
  };

  useEffect(() => { loadStats(); return () => clearInterval(pollRef.current); }, []);

  const pickReference = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) {
      toast.error("Imagem muito grande. Máx 6MB.");
      return;
    }
    const dataUrl = await fileToDataURL(f);
    setRefImage(dataUrl);
    toast.success("Imagem de referência anexada");
  };

  const pollAd = (id) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/ads/${id}`);
        if (data.status === "generating_media") {
          setStatusMsg(mode === "video" ? "Renderizando vídeo... (pode levar até 5 min)" : "Gerando imagem...");
          setAd(data);
        } else if (data.status === "ready") {
          clearInterval(pollRef.current);
          setAd(data);
          setLoading(false);
          setStatusMsg("");
          toast.success("Anúncio pronto!");
          loadStats();
        } else if (data.status === "error") {
          clearInterval(pollRef.current);
          setLoading(false);
          setStatusMsg("");
          toast.error(data.error || "Erro ao gerar anúncio");
          setAd(data);
        }
      } catch (e) {
        clearInterval(pollRef.current);
        setLoading(false);
        setStatusMsg("");
        toast.error("Erro ao verificar status");
      }
    }, POLL_INTERVAL);
  };

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Descreva seu anúncio antes de gerar."); return; }
    setLoading(true);
    setAd(null);
    setStatusMsg("Escrevendo o copy...");
    try {
      const body = {
        prompt, tone, audience, cta, mode,
        aspect,
        reference_image: refImage,
        video_model: videoModel,
        video_duration: videoDuration,
        video_size: videoSize,
      };
      const { data } = await api.post("/ads/generate", body);
      setAd(data);
      pollAd(data.id);
    } catch (e) {
      setLoading(false);
      setStatusMsg("");
      toast.error(e.response?.data?.detail || "Erro ao iniciar geração.");
    }
  };

  const copyText = (text, label) => {
    try {
      navigator.clipboard.writeText(text || "");
      toast.success(`${label} copiado!`);
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  const downloadUrl = (url, ext) => {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
    a.download = `avsl-anuncio-${Date.now()}.${ext}`;
    a.click();
  };

  const isReady = ad && ad.status === "ready";

  return (
    <div className="p-6 md:p-10 max-w-[1400px]">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <div className="overline mb-3">Gerador de Anúncios · AVSL</div>
          <h1 className="font-display text-5xl md:text-7xl leading-[0.9]">
            Crie seu <span style={{ color: "var(--avsl-red)" }}>próximo anúncio</span>
          </h1>
          <p className="mt-4 text-neutral-400 max-w-xl">
            Imagem ou vídeo. Descreva a campanha, envie uma referência (opcional) e deixe a IA fazer no estilo AVSL.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-3 min-w-[360px]">
          <Stat label="Total" value={stats.total_ads} testid="stat-total" />
          <Stat label="Hoje" value={stats.today} testid="stat-today" />
          <Stat label="Vídeos" value={stats.videos || 0} testid="stat-videos" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Form */}
        <div className="lg:col-span-5">
          <div className="avsl-card p-6 md:p-7">
            {/* Mode tabs */}
            <div className="grid grid-cols-2 gap-2 mb-6">
              <ModeBtn active={mode === "image"} onClick={() => setMode("image")} testid="mode-image" icon={ImageIcon} label="Imagem" />
              <ModeBtn active={mode === "video"} onClick={() => setMode("video")} testid="mode-video" icon={Film} label="Vídeo" />
            </div>

            <label className="overline mb-2 block">Descreva o anúncio</label>
            <textarea
              data-testid="input-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={4}
              className="avsl-input resize-none"
              placeholder={PLACEHOLDER}
            />

            <div className="grid grid-cols-2 gap-4 mt-5">
              <div>
                <label className="overline mb-2 block">Tom</label>
                <select data-testid="select-tone" value={tone} onChange={(e) => setTone(e.target.value)} className="avsl-input">
                  {TONES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="overline mb-2 block">Público</label>
                <select data-testid="select-audience" value={audience} onChange={(e) => setAudience(e.target.value)} className="avsl-input">
                  {AUDIENCES.map((a) => <option key={a}>{a}</option>)}
                </select>
              </div>
            </div>

            <div className="mt-5">
              <label className="overline mb-2 block">Call to action</label>
              <input data-testid="input-cta" value={cta} onChange={(e) => setCta(e.target.value)} className="avsl-input" placeholder="Ex: Contrate agora" />
            </div>

            {mode === "image" && (
              <>
                <div className="mt-5">
                  <label className="overline mb-2 block">Formato</label>
                  <div className="grid grid-cols-3 gap-2">
                    {IMAGE_ASPECTS.map((a) => (
                      <Chip key={a.v} active={aspect === a.v} onClick={() => setAspect(a.v)} testid={`aspect-${a.v}`}>{a.label}</Chip>
                    ))}
                  </div>
                </div>
              </>
            )}

            {mode === "video" && (
              <>
                <div className="mt-5">
                  <label className="overline mb-2 block">Modelo</label>
                  <div className="grid grid-cols-1 gap-2">
                    {VIDEO_MODELS.map((m) => (
                      <Chip key={m.v} active={videoModel === m.v} onClick={() => setVideoModel(m.v)} testid={`vmodel-${m.v}`}>{m.label}</Chip>
                    ))}
                  </div>
                </div>

                <div className="mt-5">
                  <label className="overline mb-2 block">Duração</label>
                  <div className="grid grid-cols-3 gap-2">
                    {VIDEO_DURATIONS.map((d) => (
                      <Chip key={d} active={videoDuration === d} onClick={() => setVideoDuration(d)} testid={`vdur-${d}`}>{d} seg</Chip>
                    ))}
                  </div>
                </div>

                <div className="mt-5">
                  <label className="overline mb-2 block">Formato</label>
                  <div className="grid grid-cols-2 gap-2">
                    {VIDEO_SIZES.map((s) => (
                      <Chip key={s.v} active={videoSize === s.v} onClick={() => setVideoSize(s.v)} testid={`vsize-${s.v}`}>{s.label}</Chip>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Reference image: available for BOTH modes (image = base | video = image-to-video) */}
            <div className="mt-5">
              <label className="overline mb-2 block">
                {mode === "video" ? "Foto do veículo para animar (opcional)" : "Imagem de referência (opcional)"}
              </label>
              {!refImage ? (
                <button
                  type="button"
                  data-testid="btn-upload-ref"
                  onClick={() => fileRef.current?.click()}
                  className="w-full border border-dashed p-6 flex flex-col items-center gap-2 hover:border-red-600 transition-colors duration-200"
                  style={{ borderColor: "rgba(255,255,255,0.15)" }}
                >
                  <Upload size={20} className="text-neutral-400" />
                  <span className="text-sm text-neutral-400">
                    {mode === "video"
                      ? "Envie a foto da caminhonete/carro/moto para gerar vídeo animado"
                      : "Clique para enviar (carro, moto, logo...)"}
                  </span>
                  <span className="text-xs text-neutral-600">PNG, JPG · até 6MB</span>
                </button>
              ) : (
                <div className="relative border" style={{ borderColor: "var(--avsl-border)" }} data-testid="ref-preview">
                  <img src={refImage} alt="ref" className="w-full max-h-48 object-contain bg-black" />
                  <button
                    onClick={() => setRefImage(null)}
                    data-testid="btn-remove-ref"
                    className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center border"
                    style={{ background: "rgba(0,0,0,0.7)", borderColor: "var(--avsl-red)" }}
                  >
                    <X size={14} color="#fff" />
                  </button>
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={pickReference} data-testid="input-file-ref" />
            </div>

            <button data-testid="btn-generate" onClick={generate} disabled={loading} className="avsl-btn w-full mt-7">
              {loading ? (
                <><RefreshCw size={18} strokeWidth={2.5} className="mr-2 animate-spin" /> Gerando...</>
              ) : (
                <><Send size={18} strokeWidth={2.5} className="mr-2" /> {mode === "video" ? "Gerar vídeo" : "Gerar anúncio"}</>
              )}
            </button>
            <p className="mt-3 text-xs text-neutral-500">
              {mode === "video"
                ? "A geração de vídeo pode levar de 2 a 5 minutos. Você pode navegar em outras abas."
                : "A geração da imagem leva cerca de 15–40 segundos."}
            </p>
          </div>
        </div>

        {/* Preview */}
        <div className="lg:col-span-7">
          {!ad && !loading && <EmptyState />}
          {loading && !isReady && <LoadingState message={statusMsg} mode={mode} />}
          {isReady && <AdPreview ad={ad} onCopy={copyText} onDownload={downloadUrl} />}
          {ad && ad.status === "error" && !loading && (
            <div className="avsl-card p-10 min-h-[400px] flex flex-col items-center justify-center text-center">
              <div className="font-display text-3xl text-red-500">Falha na geração</div>
              <p className="text-neutral-500 mt-3">{ad.error || "Tente novamente ou ajuste o briefing."}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ModeBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      type="button"
      className="flex items-center justify-center gap-2 py-3 border font-display-straight tracking-wider transition-colors duration-200"
      style={{
        background: active ? "#1a0808" : "transparent",
        borderColor: active ? "var(--avsl-red)" : "rgba(255,255,255,0.12)",
        color: active ? "#fff" : "#a1a1aa",
      }}
    >
      <Icon size={18} strokeWidth={2.5} />
      {label}
    </button>
  );
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      type="button"
      className="px-3 py-3 border font-display-straight tracking-wider text-sm transition-colors duration-200"
      style={{
        background: active ? "#1a0808" : "transparent",
        borderColor: active ? "var(--avsl-red)" : "rgba(255,255,255,0.12)",
        color: active ? "#fff" : "#a1a1aa",
      }}
    >
      {children}
    </button>
  );
}

function EmptyState() {
  return (
    <div className="avsl-card p-10 min-h-[500px] flex flex-col items-center justify-center text-center">
      <div className="w-16 h-16 flex items-center justify-center mb-6" style={{ background: "#1a0808", border: "1px solid var(--avsl-red)" }}>
        <Sparkles size={28} strokeWidth={2.5} color="var(--avsl-red)" />
      </div>
      <div className="font-display text-3xl">Seu anúncio aparecerá aqui</div>
      <p className="text-neutral-500 mt-3 max-w-sm">Preencha o briefing e clique em <b className="text-white">Gerar</b>.</p>
    </div>
  );
}

function LoadingState({ message, mode }) {
  return (
    <div className="avsl-card p-10 min-h-[500px] flex flex-col items-center justify-center text-center" data-testid="loading-state">
      <RefreshCw size={32} strokeWidth={2.5} className="animate-spin" color="var(--avsl-red)" />
      <div className="font-display text-3xl mt-6">
        {mode === "video" ? "Renderizando vídeo" : "Acelerando a criação"}...
      </div>
      <p className="text-neutral-500 mt-3 max-w-md">{message || (mode === "video" ? "Sora 2 processando frames — pode levar de 2 a 5 min." : "Escrevendo copy e gerando imagem.")}</p>
      <div className="mt-8 flex items-center gap-2">
        <div className="h-1 w-32 bg-neutral-800 overflow-hidden">
          <div className="h-full bg-red-600 animate-pulse" style={{ width: "60%" }} />
        </div>
        <div className="overline">Trabalhando</div>
      </div>
    </div>
  );
}

function AdPreview({ ad, onCopy, onDownload }) {
  const c = ad.copy || {};
  const hashtagsStr = Array.isArray(c.hashtags) ? c.hashtags.join(" ") : (c.hashtags || "");
  const fullText = `${c.headline || ""}\n${c.subheadline || ""}\n\n${c.description || ""}\n\n${c.caption || ""}\n\n${hashtagsStr}`.trim();
  const videoSrc = ad.video_url ? (ad.video_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${ad.video_url}` : ad.video_url) : null;

  return (
    <div className="space-y-6" data-testid="ad-preview">
      {/* Media */}
      {ad.mode === "video" && videoSrc && (
        <div className="avsl-card overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--avsl-border)" }}>
            <div className="flex items-center gap-2">
              <Video size={16} color="var(--avsl-red-soft)" />
              <span className="overline">Vídeo gerado</span>
            </div>
            <button data-testid="btn-download-video" onClick={() => onDownload(ad.video_url, "mp4")} className="text-xs uppercase tracking-widest text-neutral-400 hover:text-white flex items-center gap-1">
              <Download size={14} strokeWidth={2.5} /> Baixar
            </button>
          </div>
          <div className="p-4 bg-black flex items-center justify-center">
            <video src={videoSrc} controls autoPlay loop className="max-w-full max-h-[560px]" data-testid="ad-video" />
          </div>
        </div>
      )}
      {ad.mode === "image" && ad.image && (
        <div className="avsl-card overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--avsl-border)" }}>
            <div className="flex items-center gap-2">
              <ImageIcon size={16} color="var(--avsl-red-soft)" />
              <span className="overline">Imagem gerada</span>
            </div>
            <button data-testid="btn-download-image" onClick={() => onDownload(ad.image, "png")} className="text-xs uppercase tracking-widest text-neutral-400 hover:text-white flex items-center gap-1">
              <Download size={14} strokeWidth={2.5} /> Baixar
            </button>
          </div>
          <div className="p-4 bg-black flex items-center justify-center">
            <img src={ad.image} alt="Anúncio gerado" className="max-w-full max-h-[560px] object-contain" />
          </div>
        </div>
      )}

      {/* Copy */}
      <div className="avsl-card p-6">
        <div className="flex items-center gap-2 mb-5">
          <MessageSquare size={18} strokeWidth={2.5} color="var(--avsl-red-soft)" />
          <div className="font-display-straight tracking-wider">Copy do anúncio</div>
        </div>

        <div className="space-y-5">
          <CopyBlock label="Headline" value={c.headline} onCopy={() => onCopy(c.headline, "Headline")} big data-testid="copy-headline" />
          <CopyBlock label="Sub-headline" value={c.subheadline} onCopy={() => onCopy(c.subheadline, "Subheadline")} data-testid="copy-subheadline" />
          <CopyBlock label="Descrição" value={c.description} onCopy={() => onCopy(c.description, "Descrição")} data-testid="copy-description" />
          <CopyBlock label="Legenda Instagram" value={c.caption} onCopy={() => onCopy(c.caption, "Legenda")} multiline data-testid="copy-caption" />
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="overline flex items-center gap-1"><Hash size={12} strokeWidth={2.5} /> Hashtags</span>
              <button onClick={() => onCopy(hashtagsStr, "Hashtags")} className="text-xs uppercase tracking-widest text-neutral-400 hover:text-white">
                <Copy size={12} strokeWidth={2.5} className="inline mr-1" /> Copiar
              </button>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="hashtags">
              {(c.hashtags || []).map((h, i) => (
                <span key={i} className="px-2 py-1 text-xs border font-mono-tech" style={{ borderColor: "var(--avsl-red)", color: "var(--avsl-red-soft)" }}>{h}</span>
              ))}
            </div>
          </div>
          <div>
            <div className="overline mb-2">CTA</div>
            <div className="font-display text-2xl" style={{ color: "var(--avsl-red)" }} data-testid="copy-cta">{c.cta}</div>
          </div>
        </div>

        <button
          data-testid="btn-copy-all"
          onClick={() => onCopy(fullText, "Anúncio completo")}
          className="avsl-btn-ghost avsl-btn w-full mt-6"
        >
          <Copy size={16} strokeWidth={2.5} className="mr-2" /> Copiar tudo
        </button>
      </div>
    </div>
  );
}

function CopyBlock({ label, value, onCopy, big, multiline, ...rest }) {
  return (
    <div {...rest}>
      <div className="flex items-center justify-between mb-2">
        <span className="overline">{label}</span>
        <button onClick={onCopy} className="text-xs uppercase tracking-widest text-neutral-400 hover:text-white">
          <Copy size={12} strokeWidth={2.5} className="inline mr-1" /> Copiar
        </button>
      </div>
      <div
        className={`border-l-2 pl-4 py-1 ${big ? "font-display text-3xl leading-tight" : multiline ? "whitespace-pre-wrap text-neutral-200 text-sm" : "text-neutral-200"}`}
        style={{ borderColor: "var(--avsl-red)" }}
      >
        {value || <span className="text-neutral-500">—</span>}
      </div>
    </div>
  );
}
